import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import { Column, Divider, TextButton } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { addStage as addStageHelper, removeStage as removeStageHelper } from '~/components/ServiceBuilder/BillingStagesSelect/BillingStagesSelect.utils';
import { useLocaleKeys } from '~/i18n';
import { BillingStageScheduleType } from '~/models';
import { AutomaticStageRow } from './AutomaticStageRow';
import { withFinalStageComputed } from './finalStage';
import { defaultScheduleForType } from './scheduleForType';
import { computeTotalSumExceedsRate, getStageHelperText } from './stageValidation';
export const GetAutomaticBillingStagesTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const MAX_STAGES = 6;
export const AutomaticBillingStages = (param)=>{
    let { testId, stages, context, valueType, totalRate, effectiveDate, today, disabled, onStagesChange, onValueTypeChange } = param;
    const { AutomaticBillingStagesTestIds } = useMemo(()=>GetAutomaticBillingStagesTID(testId), [
        testId
    ]);
    const { t: stagesTexts } = useLocaleKeys('billing.stages');
    const computedStages = useMemo(()=>withFinalStageComputed({
            stages,
            valueType,
            totalRate
        }), [
        stages,
        valueType,
        totalRate
    ]);
    const totalSumExceedsRate = useMemo(()=>computeTotalSumExceedsRate(computedStages, valueType, totalRate), [
        computedStages,
        valueType,
        totalRate
    ]);
    const pushStages = (nextStages)=>{
        onStagesChange(withFinalStageComputed({
            stages: nextStages,
            valueType,
            totalRate
        }));
    };
    const handleStageChange = (index, next)=>{
        pushStages(computedStages.map((stage, i)=>i === index ? next : stage));
    };
    const handleRemove = (index)=>{
        pushStages(removeStageHelper(computedStages, index));
    };
    const handleAdd = ()=>{
        const defaultSchedule = defaultScheduleForType(BillingStageScheduleType.Manual);
        const withAdded = addStageHelper(computedStages).map((stage)=>stage.stageSchedule ? stage : _object_spread_props(_object_spread({}, stage), {
                stageSchedule: defaultSchedule
            }));
        pushStages(withAdded);
    };
    const helperTexts = computedStages.map((stage, index)=>getStageHelperText({
            stage,
            isLast: index === computedStages.length - 1,
            valueType,
            totalRate,
            totalSumExceedsRate,
            texts: stagesTexts
        }));
    const addStageButton = /*#__PURE__*/ _jsx(TextButton, {
        bi: {
            name: 'add_billing_stage_in_service_details'
        },
        skin: "primary",
        weight: "normal",
        underline: "none",
        prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
            size: 20
        }),
        disabled: disabled || computedStages.length >= MAX_STAGES,
        onClick: handleAdd,
        testId: AutomaticBillingStagesTestIds.AddStage(),
        style: {
            alignSelf: 'flex-start',
            width: 'fit-content'
        },
        children: stagesTexts.schedule.ctas.addStage()
    });
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        style: {
            marginTop: 16
        },
        testId: AutomaticBillingStagesTestIds.Root(),
        children: [
            computedStages.map((stage, index)=>{
                const isLast = index === computedStages.length - 1;
                return /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        index > 0 && /*#__PURE__*/ _jsx(Divider, {
                            margin: 0
                        }),
                        /*#__PURE__*/ _jsx(AutomaticStageRow, {
                            testId: AutomaticBillingStagesTestIds.Row(index),
                            stage: stage,
                            stageNumber: stage.stageNumber,
                            context: context,
                            effectiveDate: effectiveDate,
                            today: today,
                            isFirst: index === 0,
                            isLast: isLast,
                            canRemove: computedStages.length > 2 && index !== 0 && !isLast,
                            valueType: valueType,
                            totalRate: totalRate,
                            helperText: helperTexts[index],
                            disabled: disabled,
                            onChange: (next)=>handleStageChange(index, next),
                            onValueTypeChange: onValueTypeChange,
                            onRemove: ()=>handleRemove(index)
                        })
                    ]
                }, stage.stageNumber);
            }),
            addStageButton
        ]
    });
};
